import React from 'react'
import i18n from 'live/services/i18n'
import DateService from 'live/services/date'
import PressureTestsUtils from 'plugin-pressuretests/shared/pressure_tests/pressure_tests_utils'
import {
    PressureTestCurveCharts,
    colorMap,
    ContentInfo,
    iconMap
} from 'plugin-pressuretests/shared/components/PressureTestAnnotation'
import { TestOption } from '../types'
import DashboardLink from 'live/components/ui/DashboardLink'
import TimeRange from 'live/components/ui/time-range'
import { CSSProperties } from 'styled-components'

const PressureTestCorrelatedTestComponent = ({
    testOption,
    updateGetCharts,
    updateGetAllChartTitles,
    style
}: {
    testOption: TestOption
    updateGetCharts?: any
    updateGetAllChartTitles?: any
    style?: CSSProperties
}): React.ReactElement => {
    const resultMap = {
        failed: i18n('Failed'),
        passed: i18n('Passed'),
        done: i18n('Done')
    }
    const extra = testOption.data.extra
    const options = testOption.data.extra.options
    const testConfig = options.config
    const rangeLimitStatus = extra.rangeLimitOk ? 'passed' : 'failed'
    const uom = extra.uom

    return !testOption ? null : (
        <div className={testOption.data.uid} style={style}>
            <div className="test-info">
                <div className="live-annotation__title">{testOption.data.extra.options.config.name}</div>
                <div className="live-annotation__subtitle">
                    {`${i18n('by')} `}
                    <span className="live-annotation__author">
                        <small>{testOption.data.author || 'Missing author'}</small>
                    </span>
                    {',  '}
                    <span
                        className="live-annotation__note-time"
                        title={DateService.getMoment(testOption.data.createdAt).format(DateService.getDateTimeFormat())}
                    >
                        {DateService.fromNowCalendar(testOption.data.createdAt)}
                    </span>
                    {testOption.data.dashboard && testOption.data.dashboardId && !testOption.data.dashboardId ? (
                        <span>
                            {` ${i18n('on')} `}
                            <DashboardLink
                                name={testOption.data.dashboard}
                                id={testOption.data.dashboardId}
                                span={testOption.data.dashboardSpan}
                                begin={testOption.data.begin}
                                end={testOption.data.end}
                            />
                        </span>
                    ) : null}
                </div>
                <div className="live-annotation__time-range">
                    <TimeRange
                        color={colorMap[testOption.data.extra.result] || testOption.data.__color}
                        begin={testOption.data.begin}
                        end={testOption.data.end}
                    />
                </div>

                <ContentInfo>
                    <div className="pt-additional-information" style={{ marginTop: '20px' }}>
                        <div
                            style={{
                                display: 'flex',
                                marginTop: '20px'
                            }}
                        >
                            <p>
                                <strong>{i18n('Title')}</strong>
                            </p>
                        </div>

                        <div
                            style={{
                                clear: 'both',
                                marginBottom: '20px'
                            }}
                        >
                            {testOption.data.extra.options.title || '-'}
                        </div>

                        <div className="flex">
                            <p>
                                <strong>{i18n('Additional Information')}</strong>
                            </p>
                        </div>

                        <div style={{ clear: 'both' }}>
                            {testOption.data.extra.options.additionalInformation || '-'}
                        </div>
                    </div>
                    <div className="live-annotation__expanded-content">
                        <div className="live-annotation__dashboard">
                            <strong>Dashboard:</strong>{' '}
                            {testOption.data.dashboard && testOption.data.dashboard.length > 0
                                ? testOption.data.dashboard
                                : i18n('Missing dashboard name')}
                        </div>

                        <div className="live-annotation__title">
                            <strong>{i18n('Name')}:</strong> {testOption.data.extra.options.config.name}
                        </div>

                        <div className="pta-config">
                            <p>
                                <strong>{i18n('Fitting type')}:</strong>{' '}
                                {PressureTestsUtils.getFittingType(testOption.data.extra.options.config.fittingType)}{' '}
                            </p>
                            {(testOption.data.extra.angularCoefficient ||
                                testOption.data.extra.angularCoefficient === 0) &&
                                (testOption.data.extra.linearCoefficient ||
                                    testOption.data.extra.linearCoefficient === 0) && (
                                    <div>
                                        <p>
                                            <strong>{i18n('angular_coefficient')}: </strong>
                                            {`${testOption?.data.extra.angularCoefficient} ${testOption?.data.extra.uom ||
                                                i18n('uom')}/min`}
                                        </p>
                                        <p>
                                            <strong>{i18n('linear_coefficient')}: </strong>
                                            {testOption.data.extra.linearCoefficient}
                                        </p>
                                    </div>
                                )}
                            <p>
                                <strong>{i18n('Description')}:</strong>{' '}
                                {testOption.data.extra.options.config.description}{' '}
                            </p>
                            <p>
                                <strong>{i18n('Channel')}:</strong> {testOption.data.extra.options.curve}{' '}
                            </p>
                            <p>
                                {i18n('Result')}:{' '}
                                <span
                                    style={{
                                        color: colorMap[testOption.data.extra.result]
                                            ? colorMap[testOption.data.extra.result]
                                            : colorMap.failed
                                    }}
                                >
                                    {resultMap[testOption.data.extra.result]
                                        ? resultMap[testOption.data.extra.result]
                                        : i18n('Error')}
                                </span>
                            </p>
                            {testOption.data.extra.result !== 'done' ? (
                                <div>
                                    <p className="pta-config__test-summary">
                                        <i
                                            style={{
                                                color: colorMap[testOption.data.extra.variationOk]
                                            }}
                                            className={`fa ${iconMap[testOption.data.extra.variationOk ? 'passed' : 'failed']
                                                }`}
                                        />
                                        {i18n('Max. variation in range')}: {testOption?.data?.extra?.variation?.toFixed?.(2)} (
                                        {i18n('limit')}: {testOption.data.extra.options.config.variationTolerance}
                                        {testOption.data.extra.options.config.checkMaxPercentVariation
                                            ? '% -> t0 / ' +
                                            Math.abs(
                                                (testOption.data.extra.predictedCurve?.[0].value / 100) *
                                                testOption.data.extra.options.config.variationTolerance
                                            ).toFixed(2)
                                            : ''}
                                        )
                                    </p>

                                    {testOption.data.extra.options.config.checkVariationDecreasingEachMinute ? (
                                        <p className="pta-config__test-summary">
                                            <i
                                                style={{
                                                    color:
                                                        colorMap[
                                                        testOption.data.extra.variationDecreasingEachMinute
                                                            ? 'passed'
                                                            : 'failed'
                                                        ]
                                                }}
                                                className={`fa ${iconMap[
                                                    testOption.data.extra.variationDecreasingEachMinute
                                                        ? 'passed'
                                                        : 'failed'
                                                ]
                                                    }`}
                                            />
                                            {i18n('Variation decreasing each minute')}
                                        </p>
                                    ) : null}

                                    {testOption.data.extra.options.config.checkBeginToEndVariation ? (
                                        <p className="pta-config__test-summary">
                                            <i
                                                style={{
                                                    color:
                                                        colorMap[
                                                        testOption.data.extra.beginToEndVariationOk
                                                            ? 'passed'
                                                            : 'failed'
                                                        ]
                                                }}
                                                className={`fa ${iconMap[
                                                    testOption.data.extra.beginToEndVariationOk
                                                        ? 'passed'
                                                        : 'failed'
                                                ]
                                                    }`}
                                            />
                                            {i18n(
                                                'Last value ' +
                                                (testOption.data.extra.options.config.isNegative ? '>=' : '<=') +
                                                ' first value'
                                            ) +
                                                ' (' +
                                                i18n('tolerance') +
                                                ': ' +
                                                (testOption.data.extra.options.config.beginToEndVariationTolerance ||
                                                    '0') +
                                                ')'}
                                        </p>
                                    ) : null}

                                    {testOption.data.extra.options.config.checkDropRateLastValue ? (
                                        <p className="pta-config__test-summary">
                                            <i
                                                style={{
                                                    color:
                                                        colorMap[
                                                        testOption.data.extra.dropRateLastValueOk
                                                            ? 'passed'
                                                            : 'failed'
                                                        ]
                                                }}
                                                className={`fa ${iconMap[
                                                    testOption.data.extra.dropRateLastValueOk ? 'passed' : 'failed'
                                                ]
                                                    }`}
                                            />
                                            {i18n('Maximum drop rate (last value)') +
                                                ' = ' +
                                                testOption.data.extra.options.config.dropRateLastValueTolerance}
                                        </p>
                                    ) : null}

                                    {testConfig.checkRangeLimit &&
                                        options.minimumPressureLimit !== undefined &&
                                        options.maximumPressureLimit !== undefined ? (
                                            <p className="pta-config__test-summary">
                                                <i
                                                    style={{ color: colorMap[rangeLimitStatus] }}
                                                    className={`fa ${iconMap[rangeLimitStatus]}`}
                                                />
                                                {i18n('Pressure range between') +
                                                    ' ' +
                                                    options.minimumPressureLimit +
                                                    ' ' +
                                                    (uom || i18n('uom')) +
                                                    ' ' +
                                                    i18n('and') +
                                                    ' ' +
                                                    options.maximumPressureLimit +
                                                    ' ' +
                                                    (uom || i18n('uom'))}
                                            </p>
                                        ) : (
                                            (options.minimumPressureLimit !== undefined ||
                                                options.maximumPressureLimit !== undefined) && (
                                                <>
                                                    <p className="pta-config__test-summary">
                                                        <i
                                                            style={{ color: colorMap[rangeLimitStatus] }}
                                                            className={`fa ${iconMap[rangeLimitStatus]}`}
                                                        />
                                                        {i18n('Pressure range between') +
                                                            ' ' +
                                                            (options.minimumPressureLimit
                                                                ? options.minimumPressureLimit
                                                                : options.maximumPressureLimit) +
                                                            ' ' +
                                                            (uom || i18n('uom'))}
                                                    </p>{' '}
                                                    {(options.minimumPressureLimit !== undefined ||
                                                        options.maximumPressureLimit !== undefined) && (
                                                            <p className="pta-config__test-summary">
                                                                <i
                                                                    style={{ color: colorMap[rangeLimitStatus] }}
                                                                    className={`fa ${iconMap[rangeLimitStatus]}`}
                                                                />
                                                                {(options.minimumPressureLimit
                                                                    ? i18n('Minimum pressure')
                                                                    : i18n('Maximum pressure')) +
                                                                    ' ' +
                                                                    (options.minimumPressureLimit
                                                                        ? options.minimumPressureLimit
                                                                        : options.maximumPressureLimit) +
                                                                    ' psi'}
                                                            </p>
                                                        )}
                                                </>
                                            )
                                        )}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </ContentInfo>
            </div>

            <PressureTestCurveCharts
                curveName={testOption.data.extra.options.curve}
                extra={PressureTestsUtils.parseExtra(testOption.data.extra)}
                colorMap={colorMap}
                begin={testOption.data.begin}
                expanded={true}
                end={testOption.data.end}
                setGetCharts={fn => (updateGetCharts ? updateGetCharts(fn) : fn => { })}
                setGetAllChartTitles={fn => (updateGetAllChartTitles ? updateGetAllChartTitles(fn) : fn => { })}
            />
        </div>
    )
}

export default PressureTestCorrelatedTestComponent
